import { Component, ElementRef, ViewChild, inject, AfterViewInit, OnDestroy, Input } from '@angular/core';
import { ModalController, NavController, IonicModule, GestureController, MenuController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { Business } from '../../../core/models/Business';
import { BusinessService } from '../../../core/services/business/business.service';
import { TabService } from '../../../core/services/navigation/tab.service';
import { StateType } from '../../../core/models/StateType';
import { Subscription, skip } from 'rxjs';
import { Gesture, GestureDetail } from '@ionic/angular';

@Component({
  selector: 'app-business-selector-bottom-sheet',
  templateUrl: './business-selector-bottom-sheet.component.html',
  styleUrls: ['./business-selector-bottom-sheet.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule]
})
export class BusinessSelectorBottomSheetComponent implements AfterViewInit, OnDestroy {
  @ViewChild('gestureArea', { read: ElementRef }) gestureArea!: ElementRef;

  private modalController = inject(ModalController);
  private navController = inject(NavController);
  private businessService = inject(BusinessService);
  private tabService = inject(TabService);
  private gestureController = inject(GestureController);
  private menuController = inject(MenuController);

  @Input() permittedBusinesses: Business[] = [];
  @Input() currentBusinessId: number | null = null;
  isSwitching: boolean = false;
  imageLoadedMap: { [key: number]: boolean } = {};
  private gesture!: Gesture;
  private subscriptions: Subscription = new Subscription();

  ngAfterViewInit() {
    this.setupGesture();
  }

  ngOnDestroy() {
    if (this.gesture) {
      this.gesture.destroy();
    }
    this.subscriptions.unsubscribe();
  }

  setupGesture() {
    if (!this.gestureArea) return;

    this.gesture = this.gestureController.create({
      el: this.gestureArea.nativeElement,
      threshold: 15,
      gestureName: 'swipe-down',
      direction: 'y',
      onMove: (detail: GestureDetail) => this.onMove(detail),
      onEnd: (detail: GestureDetail) => this.onEnd(detail),
    });
    this.gesture.enable();
  }

  onMove(detail: GestureDetail) {
    if (detail.deltaY > 0) {
      this.gestureArea.nativeElement.style.transform = `translateY(${detail.deltaY}px)`;
    }
  }

  onEnd(detail: GestureDetail) {
    const shouldClose: boolean = detail.deltaY > 100;
    
    if (shouldClose) {
      this.modalController.dismiss({ selected: false });
    } else {
      this.gestureArea.nativeElement.style.transform = '';
    }
  }

  selectBusiness(business: Business) {
    if (this.isSwitching) {
      return;
    }

    this.isSwitching = true;
    const targetBusinessId: string = String(business.id);
    
    this.businessService.setCurrentBusiness(targetBusinessId);
    
    this.subscriptions.add(
      this.businessService.business$.pipe(skip(1)).subscribe({
        next: (loadedBusiness) => {
          if (loadedBusiness && loadedBusiness.id === business.id) {
            const lastPage: { url: string; state: StateType } | null = this.tabService.getLastPage('business');
            
            this.modalController.dismiss({ selected: true }).then(() => {
              this.navController.navigateRoot(lastPage?.url || '/business/business-info', {
                state: lastPage?.state || {},
              });
            });
            
            this.isSwitching = false;
          } else if (loadedBusiness === null) {
            // Handle the case where business loading failed
            this.isSwitching = false;
            this.modalController.dismiss({ selected: false });
          }
        },
        error: () => {
          this.isSwitching = false;
          this.modalController.dismiss({ selected: false });
        }
      })
    );
  }

  onImageLoad(businessId: number) {
    this.imageLoadedMap[businessId] = true;
  }

  cancel() {
    this.modalController.dismiss({ selected: false });
  }
}
